/**
 * Cached soft-glow sprites for the Jump renderer. A radial alpha falloff baked
 * into a small offscreen canvas once per colour, then blitted with `drawImage`
 * wherever a glowing dot is needed (particles, the cube's glow halo).
 *
 * This replaces per-frame `createRadialGradient` allocation — building a fresh
 * gradient every frame (and per-particle path tessellation) is one of the
 * costliest Canvas2D patterns on mobile. A `drawImage` of a pre-rendered sprite
 * is a cheap blit, and the sprite is reused across frames and call sites. The
 * cache is keyed by colour; charts use only a handful of distinct glow colours
 * (player, hazard, verdicts, milestone), so it stays tiny.
 *
 * The caller controls overall brightness via `ctx.globalAlpha` and the additive
 * look via `ctx.globalCompositeOperation` before drawing — the sprite only owns
 * the falloff SHAPE, not the final opacity or blend mode.
 */

import { hexToRgba } from './jumpRenderColour';

/**
 * Backing-store resolution of each glow sprite (px). Large enough that the cube's
 * sizeable glow halo scales up without visible softening, small enough that the
 * tiny particle dots cost almost nothing to blit.
 */
const SPRITE_SIZE = 64;

const cache = new Map<string, HTMLCanvasElement>();

/**
 * A radial-glow sprite for `colour`: an opaque-cored disc fading to fully
 * transparent at the rim. Lazily rendered and memoised by colour. Returns
 * `null` only in a non-DOM environment (the renderer is client-only, so call
 * sites can treat a non-null result as guaranteed in practice).
 */
export function getGlowSprite(colour: string): HTMLCanvasElement | null {
  const existing = cache.get(colour);
  if (existing) {
    return existing;
  }
  if (typeof document === 'undefined') {
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_SIZE;
  canvas.height = SPRITE_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const centre = SPRITE_SIZE / 2;
  const grad = ctx.createRadialGradient(centre, centre, 0, centre, centre, centre);
  // A bright, near-solid core (so a particle reads as a spark, not a faint
  // smudge) easing out to a transparent rim for the additive halo.
  grad.addColorStop(0, hexToRgba(colour, 1));
  grad.addColorStop(0.32, hexToRgba(colour, 0.92));
  grad.addColorStop(1, hexToRgba(colour, 0));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
  cache.set(colour, canvas);
  return canvas;
}

/**
 * Blit a glow sprite centred on `(x, y)` with the given on-screen `radius`. The
 * caller sets `globalAlpha` / `globalCompositeOperation` beforehand. No-ops if
 * the sprite couldn't be created (non-DOM env).
 */
export function drawGlowSprite(
  ctx: CanvasRenderingContext2D,
  colour: string,
  x: number,
  y: number,
  radius: number,
): void {
  const sprite = getGlowSprite(colour);
  if (!sprite) {
    return;
  }
  ctx.drawImage(sprite, x - radius, y - radius, radius * 2, radius * 2);
}
